import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  archiveRequest,
  createContractFromRequest,
  deleteRequest,
  rerunFirstPass,
  reviewRequest,
  submitRequest,
  unarchiveRequest,
  uploadRequestDocument,
  withdrawRequest,
} from './requestDetailApi';

function createDefaultDocUpload() {
  return {
    file: null,
    category: 'vendor_doc',
    documentType: '',
  };
}

export default function useRequestDetailActions({
  id,
  refresh,
  request,
  showToast,
  user,
}) {
  const navigate = useNavigate();
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reviewLane, setReviewLane] = useState('');
  const [reviewAction, setReviewAction] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docUpload, setDocUpload] = useState(createDefaultDocUpload);

  // Capability-based (mirrors the backend gates) so per-user grants — e.g. a
  // granted review_security — light up the same controls as the role would.
  const { can, isAdmin, canReviewRequests } = useAuth();
  const hasElevatedAccess = !!user && canReviewRequests;
  const isRequester = request?.requesterId === user?.id;
  const isArchived = Boolean(request?.archivedAt);
  // Requesters can delete their own drafts; an admin can delete any request.
  const canDelete = (isRequester && request?.status === 'draft') || isAdmin;
  const canArchive = isAdmin && !!request && !isArchived;
  const canUnarchive = isAdmin && isArchived;
  const canReviewLegal = can('review_legal') && ['submitted', 'under_review'].includes(request?.status);
  const canReviewSecurity = can('review_security') && ['submitted', 'under_review'].includes(request?.status);
  const canCreateContract = can('write_contracts') && request?.status === 'approved' && !request?.contractId;
  const canSubmit = isRequester && ['draft', 'changes_requested'].includes(request?.status);
  const canEdit = isRequester && !isArchived && ['draft', 'changes_requested'].includes(request?.status);
  const canWithdraw = isRequester && ['submitted', 'under_review'].includes(request?.status);
  const canUploadDocs = request && (isRequester || hasElevatedAccess);

  const handleSubmit = async () => {
    setActionLoading(true);
    try {
      await submitRequest(id);
      showToast('Request submitted successfully', 'success');
      refresh();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleWithdraw = async () => {
    if (!window.confirm('Withdraw this request? Legal will no longer see it as pending review.')) return;
    setActionLoading(true);
    try {
      await withdrawRequest(id);
      showToast('Request withdrawn', 'success');
      refresh();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReview = async () => {
    if (!reviewAction || !reviewLane) return;

    setActionLoading(true);
    try {
      await reviewRequest(id, { lane: reviewLane, action: reviewAction, reviewNotes });
      showToast(
        reviewAction === 'approve' ? 'Request approved' :
        reviewAction === 'reject' ? 'Request rejected' :
        'Changes requested',
        'success'
      );
      setShowReviewModal(false);
      setReviewLane('');
      setReviewAction('');
      setReviewNotes('');
      refresh();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const openReviewModal = (lane, action) => {
    setReviewLane(lane);
    setReviewAction(action);
    setReviewNotes('');
    setShowReviewModal(true);
  };

  const handleRerunFirstPass = async () => {
    setActionLoading(true);
    try {
      await rerunFirstPass(id);
      showToast('First-pass review queued', 'success');
      refresh();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDocumentUpload = async () => {
    if (!docUpload.file) {
      showToast('Choose a file to upload', 'error');
      return;
    }
    setUploadingDoc(true);
    try {
      const body = new FormData();
      body.append('file', docUpload.file);
      body.append('category', docUpload.category);
      body.append('documentType', docUpload.documentType);
      await uploadRequestDocument(id, body);
      showToast('Document uploaded', 'success');
      setDocUpload(createDefaultDocUpload());
      const input = document.getElementById('tprm-doc-upload');
      if (input) input.value = '';
      refresh();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleCreateContract = async () => {
    setActionLoading(true);
    try {
      const data = await createContractFromRequest(id);
      showToast('Contract created successfully', 'success');
      navigate(`/contracts/${data.createdContractId}`);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    setActionLoading(true);
    try {
      await deleteRequest(id);
      showToast('Request deleted', 'success');
      navigate('/tprm');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setActionLoading(false);
      setShowDeleteModal(false);
    }
  };

  const handleArchive = async () => {
    setActionLoading(true);
    try {
      await archiveRequest(id);
      showToast('Request archived', 'success');
      refresh();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnarchive = async () => {
    setActionLoading(true);
    try {
      await unarchiveRequest(id);
      showToast('Request unarchived', 'success');
      refresh();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  return {
    actionLoading,
    canArchive,
    canCreateContract,
    canDelete,
    canUnarchive,
    handleArchive,
    handleUnarchive,
    isAdmin,
    isArchived,
    canEdit,
    canReviewLegal,
    canReviewSecurity,
    canSubmit,
    canUploadDocs,
    canWithdraw,
    docUpload,
    handleCreateContract,
    handleDelete,
    handleDocumentUpload,
    handleRerunFirstPass,
    handleReview,
    handleSubmit,
    handleWithdraw,
    hasElevatedAccess,
    openReviewModal,
    reviewAction,
    reviewLane,
    reviewNotes,
    setDocUpload,
    setReviewAction,
    setReviewLane,
    setReviewNotes,
    setShowDeleteModal,
    setShowReviewModal,
    showDeleteModal,
    showReviewModal,
    uploadingDoc,
  };
}
